/**
 * TÜRKÇE YAZIM DÜZENİ
 *
 * Kullanıcı "ahmet yilmaz", "AHMET YILMAZ" ya da "AhMeT" yazsa da kayıtta
 * hep "Ahmet Yılmaz" görünsün diye alan alan normalleştirme.
 *
 * TÜRKÇE İNCELİĞİ: JavaScript'in varsayılan `toUpperCase()` fonksiyonu
 * "istanbul" → "ISTANBUL" yapar; doğrusu "İSTANBUL"dur. Aynı şekilde
 * "ILGIN" → "ilgin" olmalıdır, "ılgın" değil. Bu yüzden her yerde
 * `toLocaleUpperCase('tr-TR')` / `toLocaleLowerCase('tr-TR')` kullanılır.
 *
 * TASARIM KURALI: Normalleştirme YALNIZCA harf büyüklüğünü ve fazla boşluğu
 * düzeltir. Kelime silmez, harf değiştirmez, kısaltmayı açmaz. Kullanıcının
 * yazdığı bilgi hiçbir zaman kaybolmaz.
 */
/** Baştaki/sondaki boşlukları atar, aradaki çoklu boşlukları teke indirir. */
export function bosluklariTopla(metin) {
    return String(metin ?? '').replace(/\s+/g, ' ').trim();
}
const buyuk = (m) => m.toLocaleUpperCase('tr-TR');
const kucuk = (m) => m.toLocaleLowerCase('tr-TR');
/**
 * Sözcük Türkçe'ye özgü harf içeriyor mu? İçermiyorsa (marka ve model
 * kodlarında olduğu gibi) düz İngilizce kural uygulanır: "lci" → "LCI",
 * "audi" → "Audi". Türkçe kuralla "LCİ" ve "Audı" çıkardı.
 */
const turkceHarfliMi = (m) => /[çğıöşüÇĞİÖŞÜ]/.test(m);
const akilliBuyuk = (m) => (turkceHarfliMi(m) ? buyuk(m) : m.toUpperCase());
const akilliKucuk = (m) => (turkceHarfliMi(m) ? kucuk(m) : m.toLowerCase());
/**
 * Büyük harfle yazılması gereken kısaltmalar ve marka yazımları.
 * Buraya eklenen sözcükler olduğu gibi bırakılır.
 */
const KISALTMALAR = new Set([
    // Otomotiv / teknik
    'ECU', 'DPF', 'EGR', 'DSG', 'ABS', 'ESP', 'AGR', 'TDI', 'TSI', 'TFSI', 'FSI',
    'CDI', 'HDI', 'DCI', 'JTD', 'CRDI', 'GDI', 'MPI', 'LPG', 'CNG', 'AdBlue',
    'SCR', 'EGT', 'MAF', 'MAP', 'IAT', 'RPM', 'KM', 'HP', 'PS', 'NM', 'BAR',
    'OBD', 'OBD2', 'CAN', 'VIN', 'AWD', 'FWD', 'RWD', 'AT', 'MT',
    // Markalar (büyük yazılanlar)
    'BMW', 'VW', 'MG', 'DS', 'SEAT', 'KIA', 'GMC', 'RAM', 'BYD',
    // Kurumsal / ticari
    'KDV', 'ÖTV', 'IBAN', 'TR', 'VD', 'TC', 'AŞ', 'LTD', 'ŞTİ', 'A.Ş.', 'LTD.', 'ŞTİ.',
    'NERO', 'PDF', 'SMS',
]);
/**
 * Çok sözcüklü marka yazımları. Genel kural her sözcüğü "Örnek" biçimine
 * getirdiği için "NERO PERFORMANCE" → "NERO Performance" oluyordu; marka
 * kendi yazımını korumalı. Anahtar: küçük harfe indirilmiş, boşlukları
 * teklenmiş hâli.
 */
const MARKA_YAZIMLARI = {
    'nero performance': 'NERO PERFORMANCE',
    'neroperformance': 'NeroPerformance',
    'nero pro': 'NERO PRO',
};
/** Küçük kalması gereken bağlaçlar (satır başında değilse). */
const KUCUK_KALANLAR = new Set(['ve', 'ile', 'için', 'veya', 'da', 'de', 'mi']);
/** "ahmet-veli" → "Ahmet-Veli"; tire ve noktayla ayrılan her parça büyür. */
function tireliBuyut(parca) {
    return parca
        .split(/([-.])/)
        .map((p, i) => (i % 2 === 1 || !p ? p : buyuk(p.slice(0, 1)) + kucuk(p.slice(1))))
        .join('');
}
/** Sözcüğü "Örnek" biçimine getirir; rakam içeren parçalara dokunmaz. */
function sozcuguBuyut(sozcuk) {
    if (!sozcuk)
        return sozcuk;
    const dupDuz = sozcuk.replace(/[^\p{L}]/gu, '');
    /* Kısaltma listesindekiler olduğu gibi (büyük) yazılır. */
    if (dupDuz && KISALTMALAR.has(buyuk(dupDuz))) {
        return sozcuk.replace(dupDuz, buyuk(dupDuz));
    }
    /* Rakam içeriyorsa (320i, 1.6, 4x4, 5W-30) kullanıcının yazdığı gibi kalsın. */
    if (/\d/.test(sozcuk))
        return sozcuk;
    /* Kesme işaretinden SONRAKİ ek küçük yazılır: "Ankara'DAN" → "Ankara'dan" */
    const parcalar = sozcuk.split(/(['’])/);
    return parcalar
        .map((p, i) => {
        if (p === "'" || p === '’')
            return p;
        return i === 0 ? tireliBuyut(p) : kucuk(p);
    })
        .join('');
}
/**
 * Kişi, firma, marka gibi özel adlar: "AHMET yilmaz" → "Ahmet Yılmaz".
 * Bağlaçlar ilk sözcük değilse küçük kalır: "Ali ve Veli Otomotiv".
 */
export function ozelAd(metin) {
    const temiz = bosluklariTopla(metin);
    if (!temiz)
        return '';
    const marka = MARKA_YAZIMLARI[kucuk(temiz)];
    if (marka)
        return marka;
    return temiz
        .split(' ')
        .map((s, i) => {
        if (i > 0 && KUCUK_KALANLAR.has(kucuk(s)))
            return kucuk(s);
        return sozcuguBuyut(s);
    })
        .join(' ');
}
/**
 * Marka / model: özel ad kuralı, ama "320i" gibi kod parçaları korunur ve
 * 2–3 harfli sözcükler model kodu sayılıp büyük yazılır (LCI, GTI, RS, TDI).
 * 4+ harfliler marka adı sayılır ve normal büyütülür (audi → Audi).
 */
export function markaModel(metin) {
    const temiz = bosluklariTopla(metin);
    if (!temiz)
        return '';
    return temiz
        .split(' ')
        .map((s) => {
        if (/\d/.test(s))
            return s;
        const harf = s.replace(/[^\p{L}]/gu, '');
        if (harf.length >= 2 && harf.length <= 3)
            return akilliBuyuk(s);
        if (KISALTMALAR.has(s.toUpperCase()))
            return s.toUpperCase();
        return akilliBuyuk(s.slice(0, 1)) + akilliKucuk(s.slice(1));
    })
        .join(' ');
}
/**
 * Ürün, hizmet, kategori adı: ilk harf büyük, gerisi kullanıcının yazdığı
 * gibi kalır — "Motor yağı 5W-30" bozulmasın diye her sözcük büyütülmez.
 * Tamamı büyük yazılmışsa (BAĞIRMA) cümle düzenine çekilir.
 */
export function urunAdi(metin) {
    const temiz = bosluklariTopla(metin);
    if (!temiz)
        return '';
    const harfler = temiz.replace(/[^\p{L}]/gu, '');
    const hepsiBuyuk = harfler.length >= 4 && harfler === buyuk(harfler);
    /* Küçültürken "5W-30", "M5" gibi rakamlı kodlara dokunulmaz. */
    const govde = hepsiBuyuk
        ? temiz.split(' ').map((s) => (/\d/.test(s) ? s : kucuk(s))).join(' ')
        : temiz;
    return govde
        .split(' ')
        .map((s, i) => (i === 0 ? sozcuguBuyut(s) : (KISALTMALAR.has(buyuk(s.replace(/[^\p{L}]/gu, ''))) ? sozcuguBuyut(s) : s)))
        .join(' ');
}
/** Kategori adı ürün adıyla aynı kuralı kullanır. */
export const kategoriAdi = urunAdi;
/**
 * Serbest metin (not, açıklama): yalnızca boşluk temizliği ve ilk harf.
 * Cümle içeriğine karışılmaz.
 */
export function cumle(metin) {
    const temiz = bosluklariTopla(metin);
    if (!temiz)
        return '';
    return buyuk(temiz.slice(0, 1)) + temiz.slice(1);
}
/**
 * Plaka: "27abc123" → "27 ABC 123". Türkiye plakası tanınırsa gruplanır,
 * tanınmazsa yalnızca büyütülür — yabancı plaka bozulmasın.
 */
export function plaka(metin) {
    /* Türk plakalarında Ç Ğ İ Ö Ş Ü harfleri kullanılmaz; bu yüzden Türkçe
       değil düz büyük harf kuralı doğrudur ("i" → "I", "İ" değil). */
    const sade = String(metin ?? '').replace(/[\s-]+/g, '').toUpperCase();
    if (!sade)
        return '';
    const m = /^(\d{2})([A-ZÇĞİÖŞÜ]{1,3})(\d{2,5})$/.exec(sade);
    if (m)
        return `${m[1]} ${m[2]} ${m[3]}`;
    return bosluklariTopla(String(metin ?? '').toUpperCase());
}
/** Telefon: rakamları korur, "0546 527 27 63" biçimine getirir. */
export function telefon(metin) {
    const rakam = String(metin ?? '').replace(/\D/g, '');
    if (rakam.length === 11 && rakam.startsWith('0')) {
        return `${rakam.slice(0, 4)} ${rakam.slice(4, 7)} ${rakam.slice(7, 9)} ${rakam.slice(9)}`;
    }
    if (rakam.length === 10 && !rakam.startsWith('0')) {
        return `0${rakam.slice(0, 3)} ${rakam.slice(3, 6)} ${rakam.slice(6, 8)} ${rakam.slice(8)}`;
    }
    return bosluklariTopla(metin);
}
/**
 * E-posta her zaman küçük harf — burada TÜRKÇE kural KULLANILMAZ:
 * `toLocaleLowerCase('tr')` "Info" → "ınfo" yapar ve adresi bozar.
 */
export function eposta(metin) {
    return bosluklariTopla(metin).toLowerCase();
}
/** IBAN: boşluksuz büyük harfe çevirip 4'erli gruplar hâlinde yazar. */
export function iban(metin) {
    /* IBAN'da da Türkçe kural kullanılmaz: "tr" → "TR" olmalı, "TR" zaten doğru. */
    const sade = String(metin ?? '').replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(sade)) {
        return bosluklariTopla(String(metin ?? '').toUpperCase());
    }
    return (sade.match(/.{1,4}/g) ?? []).join(' ');
}
/** Alan türü → düzeltme fonksiyonu. Arayüz ve sunucu aynı listeyi kullanır. */
export const YAZI_DUZENI = {
    ozelAd, markaModel, urunAdi, kategoriAdi, cumle, plaka, telefon, eposta, iban,
    bosluk: bosluklariTopla,
};
/** Adı verilen kurala göre düzeltir; kural bilinmiyorsa metni olduğu gibi bırakır. */
export function duzelt(tur, metin) {
    if (!tur)
        return metin;
    const f = YAZI_DUZENI[tur];
    return f ? f(metin) : metin;
}
//# sourceMappingURL=yaziDuzeni.js.map